import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Minus, Plus, ShieldCheck, Truck, RotateCcw } from "lucide-react";
import { PRODUCT, FEATURES, FAQS } from "../config/product";
import { ttqTrack, TTQ_CONTENT } from "../lib/tiktok";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const priceStr = (n) => n.toFixed(2).replace(".", ",");

const Buy = () => {
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);

  // Detecta la vuelta desde Stripe (?paid=1 / ?canceled=1).
  useEffect(() => {
    // El usuario ve la página de producto → evento para TikTok.
    ttqTrack("ViewContent", {
      ...TTQ_CONTENT,
      value: PRODUCT.priceEur,
      currency: PRODUCT.currency,
    });

    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "1") {
      setPaid(true);
      // Compra completada → evento CLAVE para que TikTok optimice hacia compradores.
      let pending = null;
      try {
        pending = JSON.parse(localStorage.getItem("ttq_pending") || "null");
      } catch (_) {
        pending = null;
      }
      ttqTrack("CompletePayment", {
        ...TTQ_CONTENT,
        quantity: pending?.quantity || 1,
        value: pending?.value || PRODUCT.priceEur,
        currency: PRODUCT.currency,
      });
      try {
        localStorage.removeItem("ttq_pending");
      } catch (_) {
        /* noop */
      }
      toast.success("¡Gracias por tu compra! Te enviaremos la confirmación por email.");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("canceled") === "1") {
      toast("Pago cancelado. Tu carrito sigue aquí cuando quieras.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleBuy = async () => {
    setLoading(true);
    const value = Number((PRODUCT.priceEur * qty).toFixed(2));
    // El usuario inicia el pago → señal fuerte para el algoritmo de TikTok.
    ttqTrack("InitiateCheckout", {
      ...TTQ_CONTENT,
      quantity: qty,
      value,
      currency: PRODUCT.currency,
    });
    // Guardamos el importe para el evento de compra al volver de Stripe.
    try {
      localStorage.setItem("ttq_pending", JSON.stringify({ value, quantity: qty }));
    } catch (_) {
      /* noop */
    }
    try {
      const res = await axios.post(`${API}/checkout`, {
        quantity: qty,
        origin: window.location.origin,
      });
      if (res.data?.configured && res.data?.url) {
        window.location.href = res.data.url; // → Stripe Checkout
        return;
      }
      // Stripe aún no configurado (sin clave en el servidor).
      toast.error("El pago todavía no está activo. Vuelve a intentarlo en un momento.");
    } catch (err) {
      toast.error("No se pudo iniciar el pago. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const total = PRODUCT.priceEur * qty;

  return (
    <section
      id="comprar"
      data-testid="waitlist-section"
      className="relative min-h-screen py-32 px-6 md:px-10 flex items-center"
    >
      <div className="max-w-[1100px] mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Copy + FAQ */}
          <div className="lg:col-span-7">
            <span className="font-serif-accent text-white/60 text-xl">— Comprar</span>
            <h2 className="font-display text-6xl md:text-7xl leading-[0.85] font-bold tracking-tighter mt-4 mb-8">
              Protege tu
              <br />
              <span className="holo-text">batería hoy.</span>
            </h2>

            <ul className="flex flex-col gap-3 mb-10">
              {FEATURES.map((f) => (
                <li key={f.title} className="flex items-start gap-3 font-body text-white/75">
                  <span className="text-[#ff9eeb] mt-1">✦</span>
                  <span>
                    <span className="text-white font-medium">{f.title}.</span> {f.body}
                  </span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-6 text-white/50 text-sm font-body mb-12">
              <span className="flex items-center gap-2"><ShieldCheck size={16} /> Pago seguro Stripe</span>
              <span className="flex items-center gap-2"><Truck size={16} /> Envío 24-48 h</span>
              <span className="flex items-center gap-2"><RotateCcw size={16} /> Devolución 30 días</span>
            </div>

            {/* FAQ */}
            <div className="border-t border-white/10 pt-8">
              {FAQS.map((f) => (
                <details key={f.q} className="group border-b border-white/10 py-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-body font-medium text-white/90">
                    {f.q}
                    <span className="text-[#ff9eeb] transition group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 font-body text-sm text-white/60 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>

          {/* Tarjeta de compra */}
          <div className="lg:col-span-5">
            <div className="glass-strong rounded-3xl p-8 md:p-10 relative overflow-hidden sticky top-28">
              <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[#ff9eeb] opacity-10 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-[#a2e2ff] opacity-10 blur-3xl pointer-events-none" />

              <div className="relative">
                <img
                  src={PRODUCT.hero}
                  alt={PRODUCT.name}
                  className="w-full h-44 object-cover rounded-2xl mb-6"
                  loading="lazy"
                />
                <h3 className="font-display text-2xl font-bold tracking-tight">{PRODUCT.name}</h3>
                <div className="flex items-baseline gap-3 mt-2 mb-6">
                  <span className="font-display text-4xl font-bold holo-text">{priceStr(PRODUCT.priceEur)}€</span>
                  {PRODUCT.compareAtEur > PRODUCT.priceEur && (
                    <span className="font-body text-white/40 line-through">{priceStr(PRODUCT.compareAtEur)}€</span>
                  )}
                </div>

                {/* Cantidad */}
                <div className="flex items-center justify-between mb-6">
                  <span className="font-body text-xs text-white/40 uppercase tracking-[0.25em]">Cantidad</span>
                  <div className="flex items-center gap-4">
                    <button
                      data-testid="qty-minus"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-white/50 transition"
                      aria-label="Menos"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="font-display text-xl w-6 text-center">{qty}</span>
                    <button
                      data-testid="qty-plus"
                      onClick={() => setQty((q) => Math.min(10, q + 1))}
                      className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-white/50 transition"
                      aria-label="Más"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  data-testid="waitlist-submit-btn"
                  onClick={handleBuy}
                  disabled={loading}
                  className="group relative w-full py-4 rounded-full bg-white text-black font-body font-medium hover:scale-[0.98] active:scale-95 transition disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><Loader2 className="animate-spin" size={18} /> Redirigiendo al pago…</>
                  ) : (
                    <>Comprar · {priceStr(total)}€</>
                  )}
                </button>

                {paid && (
                  <p data-testid="paid-note" className="mt-4 font-body text-sm text-[#a2e2ff] text-center">
                    ✓ Pago recibido. Revisa tu email para la confirmación.
                  </p>
                )}
                <p className="mt-4 font-body text-xs text-white/40 text-center">
                  Pago con tarjeta seguro vía Stripe. No guardamos los datos de tu tarjeta.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Buy;
